/**
 * Relationship Compass — Coaching Service (Compass)
 *
 * Calls the DDD Knowledge Hub coaching endpoints.
 *
 * Australian English throughout.
 */

import { getFunctions, httpsCallableFromURL } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { authHubAuth } from './firebase';
import { moduleUserContext } from './coachModuleContext';

const APP_ID = 'relationship-compass';
const BASE_URL = 'https://australia-southeast1-ddd-knowledge-hub.cloudfunctions.net';

const URLS = {
  askCoach: `${BASE_URL}/askCoach`,
  getProfile: `${BASE_URL}/getCoachingProfile`,
  updateProfile: `${BASE_URL}/updateCoachingProfile`,
  deleteProfile: `${BASE_URL}/deleteCoachingProfile`,
  exportData: `${BASE_URL}/exportCoachingData`,
  startSession: `${BASE_URL}/startCoachingSession`,
  endSession: `${BASE_URL}/endCoachingSession`,
  forgetSession: `${BASE_URL}/forgetCoachingSession`
};

function getCallable(url) {
  try {
    const app = getApp();
    const fns = getFunctions(app, 'australia-southeast1');
    return httpsCallableFromURL(fns, url);
  } catch {
    return null;
  }
}

// Who is asking: the person's DDD account (the Auth Hub sign-in). The coach
// runs in the Knowledge Hub's own project, and the callable SDK can only
// attach this app's own sign-in, which never exists (ddd-relationship-compass
// has no Firebase Authentication). So the hub ID token goes in the payload as
// authToken, which the Knowledge Hub verifies against the hub
// (ddd-knowledge-hub/functions/support/authMiddleware.js). Same as Deeper
// Dialogues' ChattySupportService. Without it every call was a 401.
export function isSignedInForCoaching() {
  return !!authHubAuth?.currentUser;
}

async function withAuth(payload) {
  const user = authHubAuth?.currentUser;
  if (!user) return payload;
  try {
    return { ...payload, authToken: await user.getIdToken() };
  } catch {
    return payload;
  }
}

function mapError(error) {
  const code = error?.code;
  const details = error?.customData || error?.details || {};
  if (code === 'functions/unauthenticated') {
    return { success: false, error: 'signin-required', response: "You'll need to sign in to use coaching." };
  }
  if (code === 'functions/failed-precondition') {
    return { success: false, error: 'consent-required', response: 'Coaching consent is required before we can begin.' };
  }
  if (code === 'functions/resource-exhausted') {
    return {
      success: false,
      error: 'cap-exceeded',
      dailyCapExceeded: !!details.dailyCapExceeded,
      tier: details.tier,
      dailyLimit: details.dailyLimit,
      upgradeUrl: details.upgradeUrl,
      response: details.tier === 'free'
        ? "You've used your 5 free coaching messages today. Upgrade for more, or come back tomorrow."
        : "You've reached today's coaching limit. It resets at midnight."
    };
  }
  return { success: false, error: error?.message || 'unknown', response: "I'm having trouble connecting right now. Please try again in a moment." };
}

class CoachingService {
  constructor() {
    this.conversationHistory = [];
    this.sessionId = null;
  }

  resetSession() {
    this.conversationHistory = [];
    this.sessionId = null;
  }

  async getProfile() {
    const callable = getCallable(URLS.getProfile);
    if (!callable) return { success: false, error: 'not-ready' };
    try { return (await callable(await withAuth({}))).data; }
    catch (e) { return mapError(e); }
  }

  async grantConsent({ personalContext = {}, dataSharingScope = 'app-only' } = {}) {
    const callable = getCallable(URLS.updateProfile);
    if (!callable) return { success: false, error: 'not-ready' };
    try {
      return (await callable(await withAuth({
        consent: { granted: true, retentionAccepted: true, dataSharingScope },
        personalContext
      }))).data;
    } catch (e) { return mapError(e); }
  }

  async updateProfile(patch) {
    const callable = getCallable(URLS.updateProfile);
    if (!callable) return { success: false, error: 'not-ready' };
    try { return (await callable(await withAuth(patch))).data; }
    catch (e) { return mapError(e); }
  }

  async deleteAll() {
    const callable = getCallable(URLS.deleteProfile);
    if (!callable) return { success: false, error: 'not-ready' };
    try {
      const res = (await callable(await withAuth({ appId: APP_ID }))).data;
      this.resetSession();
      return res;
    } catch (e) { return mapError(e); }
  }

  async exportData() {
    const callable = getCallable(URLS.exportData);
    if (!callable) return { success: false, error: 'not-ready' };
    try { return (await callable(await withAuth({}))).data; }
    catch (e) { return mapError(e); }
  }

  async startSession({ topic = null, sessionType = 'exploration' } = {}) {
    const callable = getCallable(URLS.startSession);
    if (!callable) return { success: false, error: 'not-ready' };
    try {
      const res = (await callable(await withAuth({ appId: APP_ID, topic, sessionType }))).data;
      if (res?.sessionId) this.sessionId = res.sessionId;
      this.conversationHistory = [];
      return res;
    } catch (e) { return mapError(e); }
  }

  async endSession({ rating = null } = {}) {
    if (!this.sessionId) return { success: true };
    const callable = getCallable(URLS.endSession);
    if (!callable) return { success: false, error: 'not-ready' };
    try {
      const res = (await callable(await withAuth({
        sessionId: this.sessionId,
        appId: APP_ID,
        rating,
        transcript: this.conversationHistory
      }))).data;
      this.resetSession();
      return res;
    } catch (e) { return mapError(e); }
  }

  async askCoach(question, { mode = 'coaching', returnUrl = null, module = null } = {}) {
    const callable = getCallable(URLS.askCoach);
    if (!callable) return { success: false, error: 'not-ready', response: "Please sign in to use coaching." };
    try {
      const res = (await callable(await withAuth({
        question,
        appId: APP_ID,
        conversationHistory: this.conversationHistory,
        sessionId: this.sessionId,
        mode,
        returnUrl,
        // The module the person came from, so the coach knows what they were
        // reading (rendered by the Knowledge Hub's module context).
        userContext: moduleUserContext(module)
      }))).data;
      if (res.success) {
        if (res.sessionId) this.sessionId = res.sessionId;
        this.conversationHistory.push(
          { role: 'user', content: question },
          { role: 'assistant', content: res.response }
        );
        if (this.conversationHistory.length > 30) {
          this.conversationHistory = this.conversationHistory.slice(-30);
        }
      }
      return res;
    } catch (e) { return mapError(e); }
  }

  async forgetSession(sessionId) {
    const callable = getCallable(URLS.forgetSession);
    if (!callable) return { success: false, error: 'not-ready' };
    try { return (await callable(await withAuth({ sessionId }))).data; }
    catch (e) { return mapError(e); }
  }
}

const coachingService = new CoachingService();
export default coachingService;
export { CoachingService };
